import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { MessageSquare, Send, Clock, User } from 'lucide-react';
import { toast } from 'sonner';
import Navbar from '@/components/Navbar';
import { logActivity } from '@/lib/activityLog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper to convert relative avatar URLs to absolute
const getAvatarUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${window.location.origin}${url}`;
};

// Message categories
const MESSAGE_CATEGORIES = [
  { value: "Meal plan / recipes", label: "Meal plan / recipes" },
  { value: "Logging or tracking", label: "Logging or tracking" },
  { value: "Eating out / free meals", label: "Eating out / free meals" },
  { value: "Weight changes", label: "Weight changes" },
  { value: "Billing / account", label: "Billing / account" },
  { value: "Something else", label: "Something else" }
];

export default function Messages({ user, onLogout }) {
  const [messages, setMessages] = useState([]);
  const [coach, setCoach] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [formData, setFormData] = useState({
    category: '',
    body: ''
  });

  useEffect(() => {
    logActivity('messages_viewed');
    fetchMessages();
    fetchCoach();
  }, []);

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API}/messages`);
      setMessages(response.data);
    } catch (error) {
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const fetchCoach = async () => {
    try {
      const userResponse = await axios.get(`${API}/auth/me`);
      if (userResponse.data.coach_id) {
        const coachResponse = await axios.get(`${API}/users/${userResponse.data.coach_id}`);
        setCoach(coachResponse.data);
      }
    } catch (error) {
      console.error('Failed to load coach');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.body.trim()) {
      toast.error('Please enter a message');
      return;
    }

    setSending(true);

    try {
      const params = {
        to_user_id: coach?.id || '',
        body: formData.body
      };
      if (formData.category) params.category = formData.category;
      await axios.post(`${API}/messages`, null, {
        params
      });
      
      toast.success('Message sent!');
      setFormData({ category: '', body: '' });
      fetchMessages();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  // Get messages sorted by date (newest first for display, but we'll reverse for chat)
  const sortedMessages = [...messages].sort((a, b) => 
    new Date(a.created_at) - new Date(b.created_at)
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar user={user} onLogout={onLogout} />
        <div className="max-w-3xl mx-auto px-4 py-8">
          <p>Loading messages...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      <Navbar user={user} onLogout={onLogout} />
      
      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Messages
          </h1>
          <p className="text-gray-600">
            Message Coach {coach ? coach.first_name : 'Knut'}
          </p>
        </div>

        {/* Main Card */}
        <Card className="shadow-lg">
          <CardHeader className="border-b bg-gray-50/50">
            <CardTitle className="flex items-center gap-2 text-lg">
              <MessageSquare className="h-5 w-5 text-blue-600" />
              Conversation
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {/* Messages Thread */}
            <div className="h-[400px] overflow-y-auto p-4 space-y-4 bg-white">
              {sortedMessages.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <MessageSquare className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>No messages yet. Start a conversation!</p>
                </div>
              ) : (
                sortedMessages.map((message) => {
                  const isFromMe = message.from_user_id === user.id;
                  
                  return (
                    <div
                      key={message.id}
                      className={`flex items-end gap-2 ${isFromMe ? 'justify-end' : 'justify-start'}`}
                    >
                      {!isFromMe && (
                        <div className="w-8 h-8 rounded-full overflow-hidden bg-blue-600 text-white text-xs font-semibold flex items-center justify-center flex-shrink-0 self-end mb-6">
                          {coach?.avatar_url ? (
                            <img src={getAvatarUrl(coach.avatar_url)} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <span>{coach?.first_name?.[0]?.toUpperCase() || 'C'}</span>
                          )}
                        </div>
                      )}
                      <div className={`max-w-[80%] ${isFromMe ? 'order-2' : 'order-1'}`}>
                        {/* Category badge for client messages */}
                        {!isFromMe ? null : (
                          <div className="flex justify-end mb-1">
                            <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                              {message.category}
                            </Badge>
                          </div>
                        )}
                        
                        <div
                          className={`p-3 rounded-2xl ${
                            isFromMe
                              ? 'bg-blue-600 text-white rounded-br-md'
                              : 'bg-gray-100 text-gray-900 rounded-bl-md'
                          }`}
                        >
                          <p className="text-sm whitespace-pre-wrap">{message.body}</p>
                        </div>
                        
                        <div className={`flex items-center gap-1 mt-1 text-xs text-gray-400 ${isFromMe ? 'justify-end' : 'justify-start'}`}>
                          <Clock className="h-3 w-3" />
                          {new Date(message.created_at).toLocaleString()}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Compose Area */}
            <div className="border-t bg-gray-50 p-4">
              {/* Helper text */}
              <p className="text-sm text-gray-600 mb-4">
                Use messages for questions, clarifications, or issues. This is not live chat, but you will always get a reply.
              </p>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Category Selection */}
                <div className="space-y-2">
                  <Label htmlFor="category" className="text-sm font-medium">
                    Category <span className="text-gray-400 text-xs font-normal">(optional)</span>
                  </Label>
                  <Select
                    value={formData.category}
                    onValueChange={(value) => setFormData({...formData, category: value})}
                  >
                    <SelectTrigger data-testid="category-select" className="bg-white">
                      <SelectValue placeholder="Select a category..." />
                    </SelectTrigger>
                    <SelectContent>
                      {MESSAGE_CATEGORIES.map((cat) => (
                        <SelectItem key={cat.value} value={cat.value}>
                          {cat.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Message Input */}
                <div className="flex gap-2">
                  <Textarea
                    data-testid="message-textarea"
                    placeholder="Type your message..."
                    value={formData.body}
                    onChange={(e) => setFormData({...formData, body: e.target.value})}
                    rows={3}
                    className="flex-1 bg-white resize-none"
                  />
                  <Button
                    data-testid="send-message-button"
                    type="submit"
                    disabled={sending || !formData.body.trim()}
                    className="bg-blue-600 hover:bg-blue-700 self-end px-6"
                  >
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
              </form>
              
              {/* Footer text */}
              <p className="text-xs text-gray-500 mt-3 text-center">
                Messages are reviewed during working hours.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
